import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, BarChart3, FileText, CheckCircle2, TrendingUp, Lock,
  Layers, Building2, ArrowRight,
  Users, Target, Clock, Eye,
  AlertTriangle, FileSignature, HardHat,
  Triangle, Hash, MapPin, Brain,
  Landmark, Factory, Building, Briefcase, Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ScrollRevealSection from '@/components/landing/ScrollRevealSection';
import AnimatedCounter from '@/components/landing/AnimatedCounter';
import MonteCarloHistogram from '@/components/landing/MonteCarloHistogram';
import CurvaSChart from '@/components/landing/CurvaSChart';
import RadarComparisonChart from '@/components/landing/RadarComparisonChart';
import DemoRequestForm from '@/components/landing/DemoRequestForm';
import heroDashboard from '@/assets/landing-qa-screenshot.png';
import securityShield from '@/assets/landing/security-shield.jpg';
import logoNetuno from '@/assets/logo_netuno.png';
import footerLogo from '@/assets/landing-footer-logo.png';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';

/* ─── Helper Components ───────────────────────────────────────────────────── */

const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
  dark?: boolean;
}> = ({ children, className = '', id, dark }) => (
  <section id={id} className={`py-20 sm:py-24 px-4 sm:px-6 lg:px-8 ${dark ? 'bg-primary text-primary-foreground' : ''} ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const SectionTitle: React.FC<{
  badge?: string;
  title: string;
  subtitle?: string;
  light?: boolean;
  align?: 'center' | 'left';
}> = ({ badge, title, subtitle, light, align = 'center' }) => (
  <div className={`mb-16 ${align === 'center' ? 'text-center' : ''}`}>
    {badge && (
      <Badge variant="outline" className={`mb-4 text-xs tracking-widest uppercase px-4 py-1 font-medium ${light ? 'border-primary-foreground/30 text-primary-foreground/80' : 'border-primary/30 text-primary'}`}>
        {badge}
      </Badge>
    )}
    <h2 className={`text-3xl sm:text-4xl font-display font-bold mb-4 ${light ? 'text-primary-foreground' : 'text-foreground'}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`text-lg leading-relaxed ${align === 'center' ? 'max-w-3xl mx-auto' : 'max-w-3xl'} ${light ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

/* ─── Pillar Card (diferenciais) ──────────────────────────────────────────── */

const PillarCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="group relative bg-card rounded-xl border border-border/50 p-6 hover:border-primary/20 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
    <div className="w-11 h-11 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/12 transition-colors">
      {icon}
    </div>
    <h3 className="text-base font-display font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
  </div>
);

/* ─── Flow Step ───────────────────────────────────────────────────────────── */

const FlowStep: React.FC<{
  number: string;
  title: string;
  description: string;
  isLast?: boolean;
}> = ({ number, title, description, isLast }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-sm shrink-0">
        {number}
      </div>
      {!isLast && <div className="w-px flex-1 bg-border mt-2" />}
    </div>
    <div className="pb-8">
      <h4 className="font-display font-semibold text-foreground mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  </div>
);

/* ─── Landing Page Component ──────────────────────────────────────────────── */

const LandingPage: React.FC = () => {
  const authPath = buildPathWithPreviewToken('/auth');

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Navbar ──────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <img src={logoNetuno} alt="NetUno Logo" className="w-[72px] h-[72px] object-contain" />
            <div>
              <span className="font-display font-bold text-xl tracking-wide block" style={{ color: 'hsl(var(--brand))' }}>NetUno</span>
              <span className="text-xs text-muted-foreground tracking-wider uppercase hidden sm:block">SGPI</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#desafio" className="hover:text-foreground transition-colors">O Desafio</a>
            <a href="#solucao" className="hover:text-foreground transition-colors">Solução</a>
            <a href="#diferenciais" className="hover:text-foreground transition-colors">Diferenciais</a>
            <a href="#como-funciona" className="hover:text-foreground transition-colors">Como Funciona</a>
            <a href="#stakeholders" className="hover:text-foreground transition-colors">Para Quem</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to={authPath}>
              <Button variant="ghost" size="sm">Entrar</Button>
            </Link>
            <a href="#demo">
              <Button size="sm" className="font-medium">
                Agendar Demonstração
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 1 — HERO
      ══════════════════════════════════════════════════════════════════ */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
            {/* Left — Copy */}
            <div className="animate-fade-in">
              <Badge variant="outline" className="mb-6 text-xs tracking-widest uppercase px-4 py-1.5 font-medium border-primary/30">
                SGPI — Sistema de Governança de Projetos de Infraestrutura
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-display font-bold text-foreground leading-[1.1] mb-6">
                <span className="block">Lastro verificável para</span>
                <span className="block bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-primary)' }}>
                  decisões de capital
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mb-4 leading-relaxed">
                Alinhamento entre quem financia, quem executa e quem fiscaliza —
                com validação compartilhada e governança com lastro em cada etapa.
              </p>

              <p className="text-sm text-primary font-semibold mb-4 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Todos operam com a mesma versão da verdade.
              </p>

              <p className="text-sm text-muted-foreground mb-8">
                Integridade por arquitetura, não por processo.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4">
                <a href="#demo">
                  <Button size="lg" className="font-semibold text-base px-8 h-12">
                    Agendar Demonstração
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="#como-funciona">
                  <Button variant="outline" size="lg" className="font-semibold text-base px-8 h-12">
                    Ver Como Funciona
                  </Button>
                </a>
              </div>
            </div>

            {/* Right — Dashboard */}
            <div className="animate-slide-up relative lg:scale-105 lg:origin-right">
              <div className="rounded-xl overflow-hidden shadow-xl border border-border/50 bg-card">
                <img
                  src={heroDashboard}
                  alt="NetUno — Plataforma SGPI de Governança de Projetos de Infraestrutura"
                  className="w-full h-auto"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-card rounded-lg shadow-lg border border-border/50 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-success" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">Validação Compartilhada</div>
                  <div className="text-xs text-muted-foreground">Executor + Contratante</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <ScrollRevealSection className="mt-20">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <AnimatedCounter end={100} suffix="%" label="Desembolsos com lastro" description="Execução validada" />
              <AnimatedCounter end={2} suffix="x" label="Validação compartilhada" description="Executor + Contratante" />
              <AnimatedCounter end={100} suffix="%" label="Rastreabilidade" description="Físico-Financeira" />
              <AnimatedCounter end={3} label="Camadas integradas" description="Execução • Governança • Capital" />
            </div>
          </ScrollRevealSection>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 2 — O DESAFIO SISTÊMICO
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="desafio" className="bg-muted/30">
        <ScrollRevealSection>
          <SectionTitle
            badge="O DESAFIO SISTÊMICO"
            title="A ausência de mecanismos estruturados de validação"
            subtitle="Projetos de infraestrutura operam sem mecanismos integrados que permitam a todas as partes compartilhar dados verificáveis sobre a execução real — gerando desalinhamento, fricção e risco."
          />
        </ScrollRevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <Layers className="w-6 h-6 text-destructive" />,
              title: 'Desalinhamento entre agentes',
              desc: 'Financiador, executor e fiscal operam com dados diferentes sobre o mesmo projeto — gerando fricção e retrabalho para todas as partes.',
            },
            {
              icon: <AlertTriangle className="w-6 h-6 text-destructive" />,
              title: 'Ausência de verificabilidade',
              desc: 'Boletins de medição não possuem vínculo estruturado com a execução física, dificultando a validação por qualquer parte envolvida.',
            },
            {
              icon: <Eye className="w-6 h-6 text-destructive" />,
              title: 'Risco não compartilhado',
              desc: 'Sem mecanismo integrado de exposição, cada agente calcula o risco de forma isolada e com critérios distintos.',
            },
            {
              icon: <Clock className="w-6 h-6 text-destructive" />,
              title: 'Validação fragmentada',
              desc: 'Diferentes etapas do ciclo são validadas em sistemas distintos, sem integração nem rastreabilidade compartilhada entre as partes.',
            },
            {
              icon: <Shield className="w-6 h-6 text-destructive" />,
              title: 'Governança dependente de processo',
              desc: 'A integridade depende de disciplina individual, não de uma arquitetura que proteja igualmente todas as partes envolvidas.',
            },
            {
              icon: <BarChart3 className="w-6 h-6 text-destructive" />,
              title: 'Base documental frágil',
              desc: 'Sem trilha imutável compartilhada, disputas contratuais carecem de base verificável — prejudicando todos os stakeholders.',
            },
          ].map((item, i) => (
            <ScrollRevealSection key={i} delay={i * 80}>
              <div className="bg-card rounded-xl border border-destructive/10 p-6 h-full">
                <div className="w-10 h-10 rounded-lg bg-destructive/8 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-display font-semibold text-foreground mb-2 text-sm">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            </ScrollRevealSection>
          ))}
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 3 — A SOLUÇÃO: VALIDAÇÃO COMPARTILHADA
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="solucao">
        <ScrollRevealSection>
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-xs tracking-widest uppercase px-4 py-1 font-medium border-primary/30 text-primary">
              A SOLUÇÃO
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Infraestrutura de validação compartilhada{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-primary)' }}>com lastro verificável</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              O NetUno cria um ambiente onde todas as partes operam com os mesmos dados verificáveis —
              alinhando execução e fluxo financeiro com transparência e confiança mútua.
            </p>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Cada medição passa por aceite formal do executor e do contratante, criando consenso documental
              que fundamenta decisões de desembolso baseadas em execução validada.
            </p>
          </div>
        </ScrollRevealSection>

        {/* Três Camadas de Valor */}
        <div className="grid sm:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: <MapPin className="w-5 h-5 text-primary" />,
              title: 'Camada 1 — Execução Documentada',
              desc: 'Registro estruturado com geolocalização e evidências. Quality Gate e Planning Gate como prova de conformidade. O executor comprova sua entrega com lastro verificável.',
            },
            {
              icon: <FileSignature className="w-5 h-5 text-primary" />,
              title: 'Camada 2 — Governança Contratual',
              desc: 'Medição vinculada à execução validada por ambas as partes. Validação compartilhada com aceite formal bilateral. Trilha de auditoria imutável acessível a todos os stakeholders.',
            },
            {
              icon: <TrendingUp className="w-5 h-5 text-primary" />,
              title: 'Camada 3 — Inteligência de Capital',
              desc: 'Exposição compartilhada e mensurável em tempo real. Decisões de desembolso fundamentadas em lastro verificável. Prognóstico probabilístico com Monte Carlo P50/P90.',
            },
          ].map((item, i) => (
            <ScrollRevealSection key={i} delay={i * 60}>
              <PillarCard icon={item.icon} title={item.title} description={item.desc} />
            </ScrollRevealSection>
          ))}
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 4 — NETUNO: ALINHAMENTO ENTRE PARTES
      ══════════════════════════════════════════════════════════════════ */}
      <Section className="bg-primary" dark>
        <ScrollRevealSection>
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 text-xs tracking-widest uppercase px-4 py-1.5 font-medium border-primary-foreground/30 text-primary-foreground/80">
              A PLATAFORMA
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-primary-foreground mb-6">
              Decisões de desembolso baseadas em execução validada
            </h2>
            <p className="text-lg text-primary-foreground/70 max-w-2xl mx-auto leading-relaxed mb-12">
              O NetUno é a primeira plataforma SGPI que alinha execução e fluxo financeiro
              com validação compartilhada, lastro verificável e governança por arquitetura —
              protegendo igualmente quem financia, quem executa e quem fiscaliza.
            </p>

            <div className="grid sm:grid-cols-4 gap-6">
              {[
                { value: 'Execução Validada', desc: 'Registro com lastro e aceite bilateral' },
                { value: 'Governança com Lastro', desc: 'Validação compartilhada entre as partes' },
                { value: 'Inteligência de Capital', desc: 'Exposição compartilhada e mensurável' },
                { value: 'Auditoria Imutável', desc: 'Trilha acessível a todos os stakeholders' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-lg font-display font-bold text-primary-foreground mb-1">{item.value}</div>
                  <div className="text-xs text-primary-foreground/60">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </ScrollRevealSection>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 5 — ARQUITETURA DE CONFIANÇA
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="diferenciais">
        <ScrollRevealSection>
          <SectionTitle
            badge="ARQUITETURA DE CONFIANÇA"
            title="Mecanismos de proteção mútua"
            subtitle="Cada diferencial do NetUno é implementado na camada de dados — protegendo igualmente todas as partes envolvidas."
          />
        </ScrollRevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: <Triangle className="w-5 h-5 text-primary" />,
              title: 'Triângulo de Consistência',
              desc: 'Garante que valor contratual, planilha e cronograma estejam alinhados — evitando divergências que geram disputas entre as partes.',
            },
            {
              icon: <FileText className="w-5 h-5 text-primary" />,
              title: 'Linter de WBS',
              desc: 'Bloqueio de inconsistências estruturais antes da ativação. Protege executor e contratante de bases contratuais frágeis.',
            },
            {
              icon: <Hash className="w-5 h-5 text-primary" />,
              title: 'Baseline Imutável',
              desc: 'Preserva a referência original do investimento, protegendo executor e financiador de contestações retroativas.',
            },
            {
              icon: <Users className="w-5 h-5 text-primary" />,
              title: 'Validação Compartilhada',
              desc: 'Aceite bilateral que cria consenso documental entre as partes. Confiança mútua como fundamento de cada medição.',
            },
            {
              icon: <Brain className="w-5 h-5 text-primary" />,
              title: 'Monte Carlo Integrado',
              desc: 'Quantifica risco de prazo com lastro estatístico — permitindo antecipação colaborativa entre todos os agentes.',
            },
            {
              icon: <BarChart3 className="w-5 h-5 text-primary" />,
              title: 'Exposição de Capital',
              desc: 'Visão unificada e compartilhada que permite a todas as partes avaliar a posição financeira do projeto.',
            },
          ].map((item, i) => (
            <ScrollRevealSection key={i} delay={i * 60}>
              <PillarCard icon={item.icon} title={item.title} description={item.desc} />
            </ScrollRevealSection>
          ))}
        </div>

        {/* Curva S showcase */}
        <ScrollRevealSection delay={200}>
          <Card className="max-w-2xl mx-auto border-border/50">
            <CardContent className="p-6">
              <div className="text-center mb-4">
                <h3 className="font-display font-semibold text-foreground">Curva S — Planejado vs. Realizado</h3>
                <p className="text-xs text-muted-foreground">Alinhamento entre execução e fluxo financeiro em tempo real</p>
              </div>
              <CurvaSChart />
            </CardContent>
          </Card>
        </ScrollRevealSection>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 6 — COMO FUNCIONA
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="como-funciona" className="bg-muted/30">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <ScrollRevealSection>
            <SectionTitle
              align="left"
              badge="COMO FUNCIONA"
              title="Da execução à decisão de desembolso"
              subtitle="O NetUno alinha execução e fluxo financeiro em etapas validadas, garantindo que todas as partes compartilhem a mesma base verificável."
            />

            <div className="mt-8">
              {[
                { n: '01', title: 'Estruturação do Contrato', desc: 'Cadastro com valor global, aditivos, segregação de funções e configuração de regras de negócio — criando uma base sólida para todas as partes.' },
                { n: '02', title: 'Importação e Validação Estrutural', desc: 'Ingestão de planilhas e cronogramas com Triângulo de Consistência e Linter de WBS, garantindo alinhamento desde a origem.' },
                { n: '03', title: 'Execução Documentada', desc: 'Apontamentos em campo com geolocalização, Quality Gate e Planning Gate — o executor comprova sua entrega com lastro verificável.' },
                { n: '04', title: 'Validação Compartilhada', desc: 'Aceite formal bilateral: executor e contratante validam cada medição, criando consenso documental entre as partes.' },
                { n: '05', title: 'Alinhamento Físico-Financeiro', desc: 'EVM automático, Curva S e Monte Carlo — exposição compartilhada e mensurável para suporte à decisão conjunta.' },
                { n: '06', title: 'Lastro e Rastreabilidade', desc: 'Trilha imutável com hash SHA-256 acessível a todos os stakeholders. Base documental verificável para auditoria e prestação de contas.' },
              ].map((step, i, arr) => (
                <FlowStep key={i} number={step.n} title={step.title} description={step.desc} isLast={i === arr.length - 1} />
              ))}
            </div>
          </ScrollRevealSection>

          {/* Monte Carlo showcase */}
          <ScrollRevealSection delay={150}>
            <div className="sticky top-24 space-y-6">
              <Card className="border-border/50 overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <Badge variant="outline" className="mb-2 text-xs tracking-widest uppercase px-3 py-0.5 border-accent/30 text-accent">
                      Monte Carlo Integrado
                    </Badge>
                    <h3 className="font-display font-semibold text-lg text-foreground">Histograma de Probabilidade</h3>
                    <p className="text-sm text-muted-foreground">10.000 iterações • Distribuição PERT</p>
                  </div>
                  <MonteCarloHistogram />
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    <div className="bg-success/10 rounded-lg p-3 text-center border border-success/20">
                      <div className="text-xs text-success font-medium">P50</div>
                      <div className="text-lg font-display font-bold text-foreground">245 dias</div>
                    </div>
                    <div className="bg-primary/10 rounded-lg p-3 text-center border border-primary/20">
                      <div className="text-xs text-primary font-medium">P80</div>
                      <div className="text-lg font-display font-bold text-primary">268 dias</div>
                    </div>
                    <div className="bg-destructive/10 rounded-lg p-3 text-center border border-destructive/20">
                      <div className="text-xs text-destructive font-medium">P90</div>
                      <div className="text-lg font-display font-bold text-foreground">282 dias</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardContent className="p-6">
                  <div className="text-center mb-2">
                    <h3 className="font-display font-semibold text-foreground">Cobertura Funcional</h3>
                    <p className="text-xs text-muted-foreground">NetUno vs. alternativas de mercado</p>
                  </div>
                  <RadarComparisonChart />
                </CardContent>
              </Card>
            </div>
          </ScrollRevealSection>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 7 — VALOR PARA CADA STAKEHOLDER
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="stakeholders">
        <ScrollRevealSection>
          <SectionTitle
            badge="VALOR PARA CADA STAKEHOLDER"
            title="Proteção e alinhamento para todas as partes"
            subtitle="O NetUno cria um ambiente de validação compartilhada onde cada stakeholder encontra valor — sem conflito de interesses."
          />
        </ScrollRevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <Landmark className="w-6 h-6 text-primary" />,
              title: 'Investidores e Gestores de Ativos',
              desc: 'Decisões de desembolso fundamentadas em lastro verificável. Exposição compartilhada e mensurável. Due diligence contínua com dados reconhecidos por todas as partes.',
            },
            {
              icon: <Factory className="w-6 h-6 text-primary" />,
              title: 'Executores e Consultorias',
              desc: 'Comprovação estruturada da entrega com lastro verificável. Proteção documental em auditorias e disputas. Redução de fricção com contratantes e financiadores.',
            },
            {
              icon: <Scale className="w-6 h-6 text-primary" />,
              title: 'Administração Pública e Institucional',
              desc: 'Auditabilidade compartilhada e transparente. Base documental imutável para prestação de contas. Conformidade estrutural, não processual.',
            },
            {
              icon: <Building2 className="w-6 h-6 text-primary" />,
              title: 'Gestores de Carteira',
              desc: 'Visão consolidada de portfólio com indicadores de risco, desempenho e saldos contratuais — compartilhados entre todas as partes.',
            },
            {
              icon: <Building className="w-6 h-6 text-primary" />,
              title: 'Concessionárias e PPPs',
              desc: 'Medição com validação compartilhada, dashboards de performance por contrato e rastreabilidade completa para governança contratual.',
            },
            {
              icon: <Briefcase className="w-6 h-6 text-primary" />,
              title: 'Facilities e Serviços Recorrentes',
              desc: 'Controle de equipes com KPIs de produtividade vinculados à execução contratual. Alinhamento entre prestador e contratante.',
            },
          ].map((item, i) => (
            <ScrollRevealSection key={i} delay={i * 60}>
              <PillarCard icon={item.icon} title={item.title} description={item.desc} />
            </ScrollRevealSection>
          ))}
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 8 — PRINCÍPIO FUNDADOR
      ══════════════════════════════════════════════════════════════════ */}
      <Section className="bg-primary" dark>
        <ScrollRevealSection>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-6 text-xs tracking-widest uppercase px-4 py-1.5 font-medium border-primary-foreground/30 text-primary-foreground/80">
                PRINCÍPIO FUNDADOR
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-primary-foreground mb-6 leading-tight">
                Integridade por arquitetura — proteção para todas as partes
              </h2>
              <p className="text-lg text-primary-foreground/70 leading-relaxed mb-6">
                A integridade de um projeto de infraestrutura não pode depender da boa vontade de um agente.
                O NetUno cria um ambiente onde a arquitetura dos dados protege igualmente quem financia,
                quem executa e quem fiscaliza — com lastro verificável, validação compartilhada
                e transparência como padrão.
              </p>
              <div className="grid sm:grid-cols-2 gap-6 mt-8">
                {[
                  { icon: <Lock className="w-6 h-6" />, title: 'Imutabilidade', desc: 'Baseline protegida — referência original preservada para todas as partes.' },
                  { icon: <Shield className="w-6 h-6" />, title: 'Isolamento de Dados', desc: 'RLS nativo. Cada parte acessa apenas o que lhe compete.' },
                  { icon: <Users className="w-6 h-6" />, title: 'Segregação (SoD)', desc: '5 níveis hierárquicos com rastreabilidade completa.' },
                  { icon: <Eye className="w-6 h-6" />, title: 'Auditoria Compartilhada', desc: 'Trilha imutável acessível a todos os stakeholders.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-11 h-11 rounded-lg bg-primary-foreground/10 flex items-center justify-center shrink-0 text-primary-foreground/80">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-primary-foreground text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-primary-foreground/60">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src={securityShield}
                alt="Integridade Arquitetural — NetUno SGPI"
                className="w-72 h-72 object-cover rounded-2xl shadow-2xl opacity-90"
                loading="lazy"
              />
            </div>
          </div>
        </ScrollRevealSection>
      </Section>

      {/* ══════════════════════════════════════════════════════════════════
          SEÇÃO 9 — CTA + FORMULÁRIO
      ══════════════════════════════════════════════════════════════════ */}
      <Section id="demo">
        <ScrollRevealSection>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <Badge variant="outline" className="mb-4 text-xs tracking-widest uppercase px-4 py-1 font-medium border-primary/30 text-primary">
                AGENDAR DEMONSTRAÇÃO
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                Conheça como o NetUno alinha execução e fluxo financeiro
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Preencha o formulário abaixo e nossa equipe entrará em contato
                para agendar uma demonstração personalizada.
              </p>
            </div>

            <Card className="border-border/50">
              <CardContent className="p-8">
                <DemoRequestForm />
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground text-center mt-6">
              Sem compromisso • Demonstração personalizada • Suporte dedicado
            </p>
          </div>
        </ScrollRevealSection>
      </Section>

      {/* ─── Footer ───────────────────────────────────────────────────── */}
      <footer className="border-t border-border py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src={footerLogo} alt="NetUno" className="h-8 w-auto" />
              </div>
              <p className="text-sm text-muted-foreground">
                Previsibilidade com Lastro
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                SGPI — Sistema de Governança de Projetos de Infraestrutura
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">Plataforma</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#diferenciais" className="hover:text-foreground transition-colors">Arquitetura de Confiança</a></li>
                <li><a href="#como-funciona" className="hover:text-foreground transition-colors">Como Funciona</a></li>
                <li><a href="#stakeholders" className="hover:text-foreground transition-colors">Para Quem</a></li>
                <li><a href="#solucao" className="hover:text-foreground transition-colors">A Solução</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">Recursos</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#desafio" className="hover:text-foreground transition-colors">O Desafio</a></li>
                <li><a href="#demo" className="hover:text-foreground transition-colors">Demonstração</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">Contato</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>3ls@3lsengenhariaconsultoria.com</li>
                <li>+55 (51) 99176-9493</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-6 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} NetUno. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
